
import { useCallback } from 'react'
import type { Node } from '@xyflow/react'

type AlignDirection = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'
type DistributeAxis = 'horizontal' | 'vertical'

function getNodeWidth(node: Node): number {
  return (node.measured?.width ?? (node.style?.width as number | undefined) ?? 250)
}

function getNodeHeight(node: Node): number {
  return (node.measured?.height ?? (node.style?.height as number | undefined) ?? 200)
}

export function useNodeAlignment(
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
) {
  const alignNodes = useCallback(
    (direction: AlignDirection) => {
      setNodes((prev) => {
        const selected = prev.filter((n) => n.selected)
        if (selected.length < 2) return prev

        const selectedIds = new Set(selected.map((n) => n.id))
        let target: number

        switch (direction) {
          case 'left':
            target = Math.min(...selected.map((n) => n.position.x))
            return prev.map((n) =>
              selectedIds.has(n.id) ? { ...n, position: { ...n.position, x: target } } : n,
            )
          case 'right':
            target = Math.max(...selected.map((n) => n.position.x + getNodeWidth(n)))
            return prev.map((n) =>
              selectedIds.has(n.id)
                ? { ...n, position: { ...n.position, x: target - getNodeWidth(n) } }
                : n,
            )
          case 'center': {
            const minX = Math.min(...selected.map((n) => n.position.x))
            const maxX = Math.max(...selected.map((n) => n.position.x + getNodeWidth(n)))
            const centerX = (minX + maxX) / 2
            return prev.map((n) =>
              selectedIds.has(n.id)
                ? { ...n, position: { ...n.position, x: centerX - getNodeWidth(n) / 2 } }
                : n,
            )
          }
          case 'top':
            target = Math.min(...selected.map((n) => n.position.y))
            return prev.map((n) =>
              selectedIds.has(n.id) ? { ...n, position: { ...n.position, y: target } } : n,
            )
          case 'bottom':
            target = Math.max(...selected.map((n) => n.position.y + getNodeHeight(n)))
            return prev.map((n) =>
              selectedIds.has(n.id)
                ? { ...n, position: { ...n.position, y: target - getNodeHeight(n) } }
                : n,
            )
          case 'middle': {
            const minY = Math.min(...selected.map((n) => n.position.y))
            const maxY = Math.max(...selected.map((n) => n.position.y + getNodeHeight(n)))
            const centerY = (minY + maxY) / 2
            return prev.map((n) =>
              selectedIds.has(n.id)
                ? { ...n, position: { ...n.position, y: centerY - getNodeHeight(n) / 2 } }
                : n,
            )
          }
        }
      })
    },
    [setNodes],
  )

  const distributeNodes = useCallback(
    (axis: DistributeAxis) => {
      setNodes((prev) => {
        const selected = prev.filter((n) => n.selected)
        if (selected.length < 3) return prev

        const selectedIds = new Set(selected.map((n) => n.id))

        if (axis === 'horizontal') {
          const sorted = [...selected].sort((a, b) => a.position.x - b.position.x)
          const first = sorted[0]
          const last = sorted[sorted.length - 1]
          const totalSpan = (last.position.x + getNodeWidth(last)) - first.position.x
          const totalNodeWidth = sorted.reduce((sum, n) => sum + getNodeWidth(n), 0)
          const gap = (totalSpan - totalNodeWidth) / (sorted.length - 1)

          let currentX = first.position.x
          const posMap = new Map<string, number>()
          for (const node of sorted) {
            posMap.set(node.id, currentX)
            currentX += getNodeWidth(node) + gap
          }

          return prev.map((n) =>
            selectedIds.has(n.id) && posMap.has(n.id)
              ? { ...n, position: { ...n.position, x: posMap.get(n.id)! } }
              : n,
          )
        } else {
          const sorted = [...selected].sort((a, b) => a.position.y - b.position.y)
          const first = sorted[0]
          const last = sorted[sorted.length - 1]
          const totalSpan = (last.position.y + getNodeHeight(last)) - first.position.y
          const totalNodeHeight = sorted.reduce((sum, n) => sum + getNodeHeight(n), 0)
          const gap = (totalSpan - totalNodeHeight) / (sorted.length - 1)

          let currentY = first.position.y
          const posMap = new Map<string, number>()
          for (const node of sorted) {
            posMap.set(node.id, currentY)
            currentY += getNodeHeight(node) + gap
          }

          return prev.map((n) =>
            selectedIds.has(n.id) && posMap.has(n.id)
              ? { ...n, position: { ...n.position, y: posMap.get(n.id)! } }
              : n,
          )
        }
      })
    },
    [setNodes],
  )

  return { alignNodes, distributeNodes }
}
